import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { LinearProgress } from "@mui/material";
import { useDocumentMetaData } from "../common/components/useDocumentMetaData";
import { BIBlue } from "../assets/buildidColors";
import pageheader from "../assets/images/page-header.jpg";
import "../assets/css/Feedback.css";
import FeedbackQuestion from "./FeedbackQuestion";
import { fetchFeedbackQuestions, fetchFeedbackStatus, saveFeedbackQuestions } from "../api/FeedbackApi";

// ─── Icon SVGs (inline to avoid MUI Icons dependency) ─────────────────────────

const ThumbUpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="22" viewBox="0 0 24 24" fill="#fff">
    <path d="M2 20h2c.55 0 1-.45 1-1v-9c0-.55-.45-1-1-1H2v11zm19.83-7.12c.11-.25.17-.52.17-.8V11c0-1.1-.9-2-2-2h-5.5l.92-4.65c.05-.22.02-.46-.08-.66-.23-.45-.52-.86-.88-1.22L14 2 7.59 8.41C7.21 8.79 7 9.3 7 9.83v7.84C7 18.95 8.05 20 9.34 20h8.11c.7 0 1.36-.37 1.72-.97l2.66-6.15z" />
  </svg>
);

const ThumbDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="22" viewBox="0 0 24 24" fill="#fff">
    <path d="M22 4h-2c-.55 0-1 .45-1 1v9c0 .55.45 1 1 1h2V4zM2.17 11.12c-.11.25-.17.52-.17.8V13c0 1.1.9 2 2 2h5.5l-.92 4.65c-.05.22-.02.46.08.66.23.45.52.86.88 1.22L10 22l6.41-6.41c.38-.38.59-.89.59-1.42V6.34C17 5.05 15.95 4 14.66 4H6.56c-.71 0-1.37.37-1.73.97L2.17 11.12z" />
  </svg>
);

const RocketIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="22" viewBox="0 0 24 24" fill="#fff">
    <path d="M9.19 6.35c-2.04 2.29-3.44 5.58-3.57 5.89L2 10.69l4.05-4.05c.47-.47 1.15-.68 1.81-.55l1.33.26zM11.17 17s3.74-1.55 5.89-3.7c5.4-5.4 4.5-9.62 4.21-10.57-.95-.3-5.17-1.19-10.57 4.21C8.55 9.09 7 12.83 7 12.83L11.17 17zm6.48-3.7c.58-.58 1.09-1.19 1.53-1.82l2.13 2.13-4.05 4.05c-.47.47-1.15.68-1.81.55l-1.33-.26c2.29-2.04 3.53-4.65 3.53-4.65zM12 9a2 2 0 0 1 2-2 2 2 0 0 1 2 2 2 2 0 0 1-2 2 2 2 0 0 1-2-2zM5 18c-1 1-3 1.5-4 2 .5-1 1-3 2-4s2.5-.5 3 0 0 1-1 2z" />
  </svg>
);

const StarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="22" viewBox="0 0 24 24" fill="#fff">
    <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

const ChatIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="22" viewBox="0 0 24 24" fill="#fff">
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="44" viewBox="0 0 24 24" fill="#fff">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
  </svg>
);

// NPS face emojis as SVG
const NpsFace1 = ({ size = 38, color = "#e53935" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 0 24 24" fill={color}>
    <circle cx="15.5" cy="9.5" r="1.5" />
    <circle cx="8.5" cy="9.5" r="1.5" />
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-4-3h1c1-1.71 2.58-2.5 3-2.5s2 .79 3 2.5h1c-1.5-2.96-3.47-4-4-4s-2.5 1.04-4 4z" />
  </svg>
);

const NpsFace2 = ({ size = 38, color = "#ef6c00" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 0 24 24" fill={color}>
    <circle cx="15.5" cy="9.5" r="1.5" />
    <circle cx="8.5" cy="9.5" r="1.5" />
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-3.5-4c.73-1.22 2.06-2 3.5-2s2.77.78 3.5 2h1.68A6.01 6.01 0 0 0 12 13a6.01 6.01 0 0 0-5.18 3h1.68z" />
  </svg>
);

const NpsFace3 = ({ size = 38, color = "#fdd835" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 0 24 24" fill={color}>
    <circle cx="15.5" cy="9.5" r="1.5" />
    <circle cx="8.5" cy="9.5" r="1.5" />
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-5-5h10v2H7z" />
  </svg>
);

const NpsFace4 = ({ size = 38, color = "#66bb6a" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 0 24 24" fill={color}>
    <circle cx="15.5" cy="9.5" r="1.5" />
    <circle cx="8.5" cy="9.5" r="1.5" />
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-3.5-2c.73 1.22 2.06 2 3.5 2s2.77-.78 3.5-2H8.5z" />
  </svg>
);

const NpsFace5 = ({ size = 38, color = "#2e7d32" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 0 24 24" fill={color}>
    <circle cx="15.5" cy="9.5" r="1.5" />
    <circle cx="8.5" cy="9.5" r="1.5" />
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm-5-6c1.07 2.16 3.27 3.5 5 3.5s3.93-1.34 5-3.5H7z" />
  </svg>
);

// ─── Icon map for dynamic question rendering ──────────────────────────────────

const ICON_MAP = [ThumbUpIcon, ThumbDownIcon, RocketIcon, StarIcon, ChatIcon];

const npsFaces = [
  { value: "1", Face: NpsFace1, color: "#e53935" },
  { value: "2", Face: NpsFace2, color: "#ef6c00" },
  { value: "3", Face: NpsFace3, color: "#fdd835" },
  { value: "4", Face: NpsFace4, color: "#66bb6a" },
  { value: "5", Face: NpsFace5, color: "#2e7d32" },
];


const FEEDBACK_STATUS = {
  user_has_submitted_the_feedback: "31681ed54a36526aed1e5266fd6368e9",
  user_has_not_submitted_the_feedback: "4b3f129e3a1b0b2782e8df14aae16dfa",
  unknown_user_or_invalid_user: "dfb6144d873e7c362df58974e52b81c5",
  pending_submission: "d9c8e1a7b2f0c3e5a1f4b6d8e9f0a1b2",
};


// ─── Component ────────────────────────────────────────────────────────────────

export default function Feedback() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const userId = Number(params.userId ?? searchParams.get("userId")) || 1;
  const feedbackId = Number(params.feedbackId ?? searchParams.get("feedbackId")) || 1;

  useDocumentMetaData({
    title: "Feedback - BuildID | Share Your Experience",
    description:
      "Share your feedback about the construction industry and BuildID. Help us improve by answering a few quick questions.",
    keywords:
      "BuildID feedback, construction industry survey, BuildID review",
    canonical: "https://mybuilderid.com/feedback",
    robots: "index, follow",
    author: "MyBuilderID Team",
    ogTitle: "Feedback - BuildID",
    ogDescription:
      "Help us improve BuildID by sharing your experience and feedback.",
    ogImage:
      "https://mybuilderid.com/static/media/logo01.a7fc7fdc78443fa49049.png",
    ogUrl: "https://mybuilderid.com/feedback",
    twitterCard: "summary_large_image",
    twitterTitle: "Feedback - BuildID | Share Your Experience",
    twitterDescription:
      "Help us improve BuildID by sharing your experience and feedback.",
    twitterImage:
      "https://mybuilderid.com/static/media/logo01.a7fc7fdc78443fa49049.png",
  });

  // Questions fetched from API
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // Answers state – keyed by question Id
  // For checkbox questions: { [questionId]: string[] }
  // For rating questions: { [questionId]: string }
  // For description questions: { [questionId]: string }
  const [answers, setAnswers] = useState({});
  // "Other" text values – keyed by question Id
  const [otherTexts, setOtherTexts] = useState({});

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(FEEDBACK_STATUS.pending_submission);

  const [toast, setToast] = useState(null);

  // ─── Fetch questions on mount ──────────────────────────────────────────────

  const checkFeedbackStatus = async () => {
    try {
      const res = await fetchFeedbackStatus(feedbackId, userId);
      if(res.message){
        setSubmitted(res.message);
        return res.message;
      }else{
        setSubmitted(FEEDBACK_STATUS.unknown_user_or_invalid_user);
        return FEEDBACK_STATUS.unknown_user_or_invalid_user;
      }
    } catch (error) {
      console.error("Error fetching feedback status:", error);
      setSubmitted(FEEDBACK_STATUS.unknown_user_or_invalid_user);
    }
  }


  useEffect(() => {
    let cancelled = false;

    async function loadQuestions() {
      try {
        setLoading(true);
        setFetchError(null);
        let a = await checkFeedbackStatus();
        if (a !== FEEDBACK_STATUS.user_has_not_submitted_the_feedback) {
          return
        }
        const data = await fetchFeedbackQuestions(userId);

        if (cancelled) return;

        if (data && Array.isArray(data)) {
          setQuestions(data);

          // Initialize answers state for each question
          const initialAnswers = {};
          const initialOtherTexts = {};
          data.forEach((q) => {
            if (q.type === "checkbox") {
              initialAnswers[q.id] = [];
            } else if (q.type === "rating") {
              initialAnswers[q.id] = "";
            } else if (q.type === "text") {
              initialAnswers[q.id] = "";
            } else {
              initialAnswers[q.id] = [];
            }
            initialOtherTexts[q.id] = "";
          });
          setAnswers(initialAnswers);
          setOtherTexts(initialOtherTexts);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to fetch feedback questions:", err);
          setFetchError("Failed to load questions. Please try again later.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadQuestions();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  // ─── Derived state ─────────────────────────────────────────────────────────

  const totalSteps = questions.length;

  const completedQuestions = questions.filter((q) => {
    const answer = answers[q.id];
    if (!answer) return false;

    if (q.type === "checkbox") {
      const hasSelections = answer.length > 0;
      const otherSelected = answer.includes("other");
      const otherFilled = !otherSelected || (otherTexts[q.id] || "").trim() !== "";
      return hasSelections && otherFilled;
    } else if (q.type === "rating") {
      return answer !== "";
    } else if (q.type === "text") {
      return answer.trim() !== "";
    }
    return false;
  }).length;

  const progress = totalSteps > 0 ? (completedQuestions / totalSteps) * 100 : 0;
  const isFormValid = totalSteps > 0 && completedQuestions === totalSteps;

  // Pre-select from URL params
  useEffect(() => {
    const selected = searchParams.get("opt");
    if (selected && questions.length > 0) {
      // Apply to first checkbox question
      const firstCheckbox = questions.find((q) => q.type === "checkbox");
      if (firstCheckbox) {
        setAnswers((prev) => ({
          ...prev,
          [firstCheckbox.id]: [selected],
        }));
      }
    }
  }, [searchParams, questions]);

  // Auto-hide toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // ─── Helpers ───────────────────────────────────────────────────────────────

  /** Parse the Options JSON string from the API into an array of { value, label } */
  const parseOptions = (optionsStr) => {
    let options = [];

    try {
      if (typeof optionsStr === "string") {
        options = JSON.parse(optionsStr);
      } else if (Array.isArray(optionsStr)) {
        options = optionsStr;
      }

      if (Array.isArray(options)) {
        return options.map((label) => ({
          value: label.toLowerCase().replace(/\s+/g, "_"),
          label: label,
        }));
      }
    } catch (e) {
      console.error("Failed to parse options:", e);
    }
    return [];
  };

  /** Get the icon component for a question by its index */
  const getQuestionIcon = (index) => {
    return ICON_MAP[index % ICON_MAP.length];
  };

  /** Update answer for a specific question */
  const updateAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  /** Update other text for a specific question */
  const updateOtherText = (questionId, value) => {
    setOtherTexts((prev) => ({ ...prev, [questionId]: value }));
  };

  // ─── Submit handler ────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Build the SaveList from questions and answers
      const saveList = questions.map((q) => {
        const answer = answers[q.id];
        let answerValue = "";

        if (q.type === "checkbox") {
          // Include "other" text if selected
          const selectedOptions = answer.map((val) => {
            if (val === "other") return `Other: ${otherTexts[q.id] || ""}`;
            // Map value back to original label
            const opts = parseOptions(q.options);
            const opt = opts.find((o) => o.value === val);
            return opt ? opt.label : val;
          });
          answerValue = JSON.stringify(selectedOptions);
        } else if (q.type === "rating") {
          answerValue = JSON.stringify([answer]);
        } else if (q.type === "text") {
          answerValue = JSON.stringify([answer]);
        }

        return {
          Id: 0,
          QuestionId: q.id,
          Answer: answerValue,
          UserId: userId,
          FeedbackId: feedbackId,
          ModifiedBy: userId,
        };
      });

      await saveFeedbackQuestions(userId, feedbackId, saveList);
      setSubmitted(FEEDBACK_STATUS.user_has_submitted_the_feedback);
      setToast({ message: "Thank you for your feedback!", severity: "success" });
    } catch (error) {
      console.error("handleSubmit-> Error submitting feedback:", error);
      setToast({ message: "Failed to submit. Please try again.", severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSubmitted(FEEDBACK_STATUS.pending_submission);
    const resetAnswers = {};
    const resetOtherTexts = {};
    questions.forEach((q) => {
      if (q.type === "checkbox") {
        resetAnswers[q.id] = [];
      } else {
        resetAnswers[q.id] = "";
      }
      resetOtherTexts[q.id] = "";
    });
    setAnswers(resetAnswers);
    setOtherTexts(resetOtherTexts);
  };

  // ─── Status-based Views ───────────────────────────────────────────────────

  // Success: user just submitted feedback now
  if (submitted === FEEDBACK_STATUS.user_has_submitted_the_feedback) {
    return (
      <>
        <section
          className="hero-area bg_img"
          style={{ backgroundImage: `url(${pageheader})` }}
        >
          <div className="container">
            <h1 className="title m-0">We Want to Hear From You!</h1>
          </div>
        </section>

        <section className="feedback-section">
          <div className="container">
            <div className="feedback-card">
              <div className="feedback-success">
                <div className="feedback-success-icon">
                  <CheckCircleIcon />
                </div>
                <h2>Thank you for your response!</h2>
                <p>
                  Your feedback helps us make BuildID better.
                  <br />
                  <br />
                  25 BuilderBucks will be added to your BuildID account shortly,
                  redeemable as entries into our prize draws. Prize draw winners
                  will be contacted within one week of closing.
                </p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  // Error: unknown or invalid user
  if (submitted === FEEDBACK_STATUS.unknown_user_or_invalid_user) {
    return (
      <>
        <section
          className="hero-area bg_img"
          style={{ backgroundImage: `url(${pageheader})` }}
        >
          <div className="container">
            <h1 className="title m-0">We Want to Hear From You!</h1>
          </div>
        </section>

        <section className="feedback-section">
          <div className="container">
            <div className="feedback-card">
              <div className="feedback-success feedback-error-state">
                <div className="feedback-error-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" height="44" viewBox="0 0 24 24" fill="#fff">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                  </svg>
                </div>
                <h2>Unable to Load Feedback</h2>
                <p>
                  We couldn't verify your identity. The link you used may be
                  invalid or expired.
                  <br />
                  <br />
                  Please check that you're using the correct feedback link from
                  your email, or contact support for assistance.
                </p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  // ─── Loading State ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <>
        <section
          className="hero-area bg_img"
          style={{ backgroundImage: `url(${pageheader})` }}
        >
          <div className="container">
            <h1 className="title m-0">We Want to Hear From You!</h1>
          </div>
        </section>

        <section className="feedback-section">
          <div className="container">
            <div className="feedback-card" style={{ textAlign: "center", padding: "60px 20px" }}>
              <LinearProgress
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "#e8e8e8",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 3,
                    background: `linear-gradient(90deg, ${BIBlue}, #1a5ce8)`,
                  },
                  maxWidth: 300,
                  margin: "0 auto 20px",
                }}
              />
              <p style={{ color: "#8D8D8D", fontSize: "1.05rem" }}>Loading questions...</p>
            </div>
          </div>
        </section>
      </>
    );
  }

  // ─── Error State ──────────────────────────────────────────────────────────

  if (fetchError) {
    return (
      <>
        <section
          className="hero-area bg_img"
          style={{ backgroundImage: `url(${pageheader})` }}
        >
          <div className="container">
            <h1 className="title m-0">We Want to Hear From You!</h1>
          </div>
        </section>

        <section className="feedback-section">
          <div className="container">
            <div className="feedback-card" style={{ textAlign: "center", padding: "60px 20px" }}>
              <p style={{ color: "#e53935", fontSize: "1.05rem", marginBottom: "20px" }}>
                {fetchError}
              </p>
              <button
                className="feedback-submit-btn"
                onClick={() => window.location.reload()}
                style={{ maxWidth: 200 }}
              >
                Retry
              </button>
            </div>
          </div>
        </section>
      </>
    );
  }

  // ─── Main Form ────────────────────────────────────────────────────────────

  return (
    <>
      {/* Hero Banner */}
      <section
        className="hero-area bg_img"
        style={{ backgroundImage: `url(${pageheader})` }}
      >
        <div className="container">
          <h1 className="title m-0">We Want to Hear From You!</h1>
        </div>
      </section>

      {/* Feedback Form */}
      <section className="feedback-section">
        <div className="container">
          {/* Intro Text */}
          <div className="feedback-intro-text" style={{ textAlign: "center", marginBottom: "40px" }}>
            <p style={{ color: "#8D8D8D", fontSize: "1.05rem", maxWidth: "600px", margin: "0 auto", lineHeight: 1.6 }}>
              Please complete this quick survey to receive 25 BuilderBucks for entries
              to win prizes including a big screen TV and various gift cards.
            </p>
          </div>
          {/* Progress */}
          <div className="feedback-progress-wrapper">
            <div className="feedback-progress-header">
              <span className="feedback-progress-text">
                {completedQuestions} of {totalSteps} questions answered
              </span>
              <span className="feedback-progress-percent">
                {Math.round(progress)}%
              </span>
            </div>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 6,
                borderRadius: 3,
                backgroundColor: "#e8e8e8",
                "& .MuiLinearProgress-bar": {
                  borderRadius: 3,
                  background: `linear-gradient(90deg, ${BIBlue}, #1a5ce8)`,
                },
              }}
            />
          </div>

          <div className="feedback-card">
            {questions.map((q, index) => {
              const questionIcon = getQuestionIcon(index);
              const options = parseOptions(q.options);

              // Determine props based on question type
              const commonProps = {
                id: `feedback-question-${q.id}`,
                icon: questionIcon,
                number: index + 1,
                title: q.question,
                subtitle: q.questionDescription || undefined,
                type: q.type,
              };

              if (q.type === "checkbox") {
                return (
                  <FeedbackQuestion
                    key={q.id}
                    {...commonProps}
                    options={options}
                    value={answers[q.id] || []}
                    onChange={(val) => updateAnswer(q.id, val)}
                    hasOther={q.otherOptions === true}
                    otherValue={otherTexts[q.id] || ""}
                    onOtherChange={(val) => updateOtherText(q.id, val)}
                    gridCols={options.length > 8 ? 2 : 2}
                  />
                );
              }

              if (q.type === "rating") {
                return (
                  <FeedbackQuestion
                    key={q.id}
                    {...commonProps}
                    faces={npsFaces}
                    value={answers[q.id] || ""}
                    onChange={(val) => updateAnswer(q.id, val)}
                  />
                );
              }

              if (q.type === "text") {
                return (
                  <FeedbackQuestion
                    key={q.id}
                    {...commonProps}
                    value={answers[q.id] || ""}
                    onChange={(val) => updateAnswer(q.id, val)}
                  />
                );
              }

              // Default fallback for unknown types – treat as checkbox
              return (
                <FeedbackQuestion
                  key={q.id}
                  {...commonProps}
                  type="checkbox"
                  options={options}
                  value={answers[q.id] || []}
                  onChange={(val) => updateAnswer(q.id, val)}
                  hasOther={q.otherOptions === true}
                  otherValue={otherTexts[q.id] || ""}
                  onOtherChange={(val) => updateOtherText(q.id, val)}
                />
              );
            })}

            {/* Submit Button */}
            <div className="feedback-submit-wrapper">
              <button
                id="feedback-submit-button"
                className="feedback-submit-btn"
                onClick={handleSubmit}
                disabled={submitting || !isFormValid}
              >
                {submitting ? (
                  <div className="spinner" />
                ) : (
                  "Submit Feedback"
                )}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Toast */}
      {toast && (
        <div className={`feedback-toast ${toast.severity}`}>
          {toast.message}
        </div>
      )}
    </>
  );
}
